// Mock data for the CSR home dashboard (/home/csr — the command-centre
// "Portfolio board" — see docs/features/csr-command-centre/).
// All dates are relative to now so the screen looks live on any day.
// Realistic for a print-services portfolio of ~150 active jobs.
//
// Comms counts (email_count/unread_email_count/meeting_count per job,
// upcoming_meetings) fold in what used to be a separate
// mock-composed fixture (mock-data/csr-command-centre.ts, now retired —
// its data lives here so there is ONE csr-home mock, matching the real
// backend's single enriched GET /views/csr-home). A handful of "narrative"
// jobs carry hand-authored comms detail matching the product brief's card
// examples; the rest of the portfolio gets deterministic, realistic-looking
// comms counts derived from the job number so the whole board is populated
// without hand-authoring dozens of email threads.

import type {
  CsrHomeDashboard,
  InFlightBoardSearchResponse,
  SalesOrderNeedingAttentionItem,
  Ticket,
  TicketItem,
  UpcomingMeetingItem,
  WorkUnitSummaryItem,
  WorkAlertItem,
} from "../data/api-types";
import type { InFlightBoardSearchOptions } from "../data/adapter";
import { csrCommandCentreCalendarEvents } from "./communications";
import { mockTickets } from "./tickets";
import { mockCustomers } from "./customers";
import { mockBookedOrders } from "./bookedOrders";
import { addWorkingDays } from "./fixture-weekdays";
import { READINESS_LADDER } from "../domain/readiness";

const d = (offsetDays: number): string =>
  new Date(Date.now() + offsetDays * 86_400_000).toISOString();

const hoursAgo = (hours: number): string =>
  new Date(Date.now() - hours * 3600_000).toISOString();

// Deterministic-but-varied hash of a job number, used for both the comms
// fallback below and the default ordered quantity — no Math.random() so
// nothing jitters on re-render.
function hashJobNo(jobNo: string): number {
  let hash = 0;
  for (let i = 0; i < jobNo.length; i++) {
    hash = (hash * 31 + jobNo.charCodeAt(i)) >>> 0;
  }
  return hash;
}

// Realistic print-run quantity range (250-5000, in round-ish steps), derived
// from the job number so the board looks populated without hand-authoring a
// quantity for every job.
function defaultQuantity(jobNo: string): number {
  return 250 + (hashJobNo(jobNo) % 20) * 250;
}

// Default top_alert_label for a job that doesn't override one explicitly.
// The real backend's invariant is "null exactly when rag === green" — most
// jobs in the fixed deadline buckets below don't carry a hand-authored
// alert, so derive a plausible one from rag to keep the mock honest. Jobs
// that need a specific named alert (or deliberately none, to exercise the
// no-alert fallback wording) pass top_alert_label explicitly and win via
// the override spread below.
function defaultTopAlertLabel(rag: string): string | null {
  if (rag === "red") return "Past due date";
  if (rag === "amber") return "Due date approaching";
  return null;
}

// Realistic Tharstern production-stage labels (last_milestone, product
// prefix stripped — see job_readiness.STRIP_PRODUCT_PREFIX_SQL) for
// readiness: "in_production" fixtures. Picked from the milestone-type
// ladder (job_readiness.py): 700 Plates Made, 800 Printing Started, 900
// Printing Finished, 1000 Finishing Started.
const _IN_PRODUCTION_STAGE_LABELS = [
  "Plates Made",
  "Printing Started",
  "Printing Finished",
  "Finishing Started",
];

function defaultStageLabel(jobNo: string, readiness: string): string | null {
  if (readiness !== "in_production") return null;
  return _IN_PRODUCTION_STAGE_LABELS[
    hashJobNo(jobNo) % _IN_PRODUCTION_STAGE_LABELS.length
  ];
}

function defaultStageUpdatedAt(
  jobNo: string,
  readiness: string,
): string | null {
  if (readiness !== "in_production") return null;
  // 0-6 days ago, deterministic — matches the card's "Last update today / N
  // days ago" wording across a realistic spread.
  return hoursAgo((hashJobNo(jobNo) % 7) * 24);
}

// Readiness values that still precede a press — mirrors the backend's
// identical set in views_module.api.transformers.csr_home._compute_attention
// (kept independently, not imported: this is a display-data fixture, not
// the production code path).
const _NOT_YET_IN_PRODUCTION = new Set([
  "not_production_ready",
  "out_for_proof",
  "production_ready",
]);

/** Mirrors `_compute_attention` (backend) so the mock's `attention` default
 *  agrees with the real rule: red = overdue (not year-suspect, not
 *  finished) or an open red alert; amber = an open amber alert, or due
 *  today/soon while not yet in production; green otherwise. Takes the
 *  already-known override values (rag/required_at/readiness/
 *  needed_by_year_suspect/isFinished) rather than the whole built object,
 *  since `job()` computes this before `overrides` is spread onto the
 *  returned record. */
function defaultAttention(o: {
  rag: string;
  requiredAt: string | null;
  neededByYearSuspect: boolean;
  readiness: string;
  isFinished: boolean;
}): "red" | "amber" | "green" {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = o.requiredAt ? new Date(o.requiredAt) : null;
  if (due) due.setHours(0, 0, 0, 0);

  const isOverdue =
    !o.isFinished &&
    due !== null &&
    due.getTime() < today.getTime() &&
    !o.neededByYearSuspect;
  if (isOverdue || o.rag === "red") return "red";

  const dueSoonCutoff = new Date(today);
  dueSoonCutoff.setDate(dueSoonCutoff.getDate() + 3);
  const isDueSoonOrToday =
    due !== null &&
    due.getTime() >= today.getTime() &&
    due.getTime() <= dueSoonCutoff.getTime();
  if (
    o.rag === "amber" ||
    (isDueSoonOrToday &&
      !o.isFinished &&
      _NOT_YET_IN_PRODUCTION.has(o.readiness))
  ) {
    return "amber";
  }
  return "green";
}

// Default open_alert_count for a job that doesn't override one explicitly —
// same derive-from-rag spirit as defaultTopAlertLabel, so the two never
// disagree about whether an alert exists (0 iff top_alert_label is null).
function defaultOpenAlertCount(rag: string): number {
  return rag === "green" ? 0 : 1;
}

function job(
  overrides: Partial<WorkUnitSummaryItem> &
    Pick<
      WorkUnitSummaryItem,
      "id" | "job_no" | "customer_name" | "job_title" | "rag"
    >,
): WorkUnitSummaryItem {
  const customerId =
    overrides.customer_id ??
    `c-${overrides.customer_name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`;
  const readiness = overrides.readiness ?? "in_production";
  const isFinished =
    overrides.close_state != null || overrides.completion_state != null;
  return {
    customer_id: customerId,
    customer_url: `/customers/${customerId}`,
    required_at: null,
    // SPA nav route for the merged job workspace page — keyed on job_no,
    // not the internal id (matches the real /views/csr-home transformer).
    self_url: `/jobs/${overrides.job_no}`,
    completion_state: null,
    needed_by_year_suspect: false,
    booked_by_you: false,
    close_state: null,
    email_count: 0,
    unread_email_count: 0,
    meeting_count: 0,
    quantity: defaultQuantity(overrides.job_no),
    // Readiness axis — deliberately separate from RAG urgency (see
    // api-types.ts). Most fixtures below are already progressing through
    // production, so default to in_production; the not_production_ready
    // lane and a handful of narrative jobs override this explicitly.
    readiness: "in_production",
    readiness_since: null,
    waiting_reason: null,
    work_kind: null,
    top_alert_label: defaultTopAlertLabel(overrides.rag),
    open_alert_count: defaultOpenAlertCount(overrides.rag),
    stage_label: defaultStageLabel(overrides.job_no, readiness),
    stage_updated_at: defaultStageUpdatedAt(overrides.job_no, readiness),
    attention: defaultAttention({
      rag: overrides.rag,
      requiredAt: overrides.required_at ?? null,
      neededByYearSuspect: overrides.needed_by_year_suspect ?? false,
      readiness,
      isFinished,
    }),
    ...overrides,
  };
}

// ---------------------------------------------------------------------------
// Comms counts — hand-authored for the jobs the product brief's card
// examples reference (keyed by job_no); everything else gets a
// deterministic-but-varied fallback derived from the job number (not
// Math.random()) so counts don't jitter on re-render.
// ---------------------------------------------------------------------------

type CommsCounts = Pick<
  WorkUnitSummaryItem,
  "email_count" | "unread_email_count" | "meeting_count"
>;

const NARRATIVE_COMMS: Record<string, CommsCounts> = {
  // Acme — overdue 2 days, customer chasing, unread.
  "729645": { email_count: 1, unread_email_count: 1, meeting_count: 0 },
  // Brightside — overdue 1 day, stuck at Print, no fresh comms.
  "729404": { email_count: 0, unread_email_count: 0, meeting_count: 0 },
  // Brightside — exceptions alert (wa-003), customer chasing.
  "730033": { email_count: 1, unread_email_count: 1, meeting_count: 0 },
  // Meridian Group — due today, tied to today's calendar meeting.
  "730005": { email_count: 0, unread_email_count: 0, meeting_count: 1 },
  // Kenton Care — due today, unread email, tied to today's meeting.
  "730001": { email_count: 2, unread_email_count: 1, meeting_count: 1 },
  // NHS Trust — at risk, older thread, two emails.
  "730023": { email_count: 2, unread_email_count: 0, meeting_count: 0 },
};

function fallbackCommsCounts(jobNo: string): CommsCounts {
  const hash = hashJobNo(jobNo);
  const emailCount = hash % 4; // 0-3
  const unreadCount = emailCount > 0 && hash % 5 === 0 ? 1 : 0;
  const meetingCount = hash % 7 === 0 ? 1 : 0;
  return {
    email_count: emailCount,
    unread_email_count: unreadCount,
    meeting_count: meetingCount,
  };
}

function withComms(unit: WorkUnitSummaryItem): WorkUnitSummaryItem {
  return {
    ...unit,
    ...(NARRATIVE_COMMS[unit.job_no] ?? fallbackCommsCounts(unit.job_no)),
  };
}

// ---------------------------------------------------------------------------
// Overdue — past required_at, rag red
// ---------------------------------------------------------------------------

export const overdueJobs: WorkUnitSummaryItem[] = [
  job({
    id: "cw-729645",
    job_no: "729645",
    customer_name: "Acme Ltd",
    customer_id: "c-acme",
    job_title: "A4 brochures — corporate template",
    rag: "red",
    required_at: d(-2),
    // Visibility-provenance example (docs/features/csr-account-allocation/)
    // — this job belongs to a colleague currently on cover, not the viewer.
    visibility_reason: "covering",
    covering_for_user_name: "Priya Shah",
    cover_until: d(5),
  }),
  job({
    id: "cw-729404",
    job_no: "729404",
    customer_name: "Brightside Health",
    customer_id: "c-brightside",
    job_title: "Patient packs — May intake",
    rag: "red",
    required_at: d(-1),
  }),
  // Real job 734493 — already in production (readiness has nothing to add
  // here), but 38 days overdue with the top work alert naming the reason
  // instead of a bare RAG word (see PortfolioBoard's reason line).
  job({
    id: "cw-734493",
    job_no: "734493",
    customer_name: "Bunzl UK Ltd",
    job_title: "Branded packaging inserts",
    rag: "red",
    required_at: d(-38),
    quantity: 800,
    readiness: "in_production",
    top_alert_label: "Past due date",
  }),
  // Modelled on a real staging job ~15 days overdue — a small point-of-sale
  // print run for a retail chain, stuck well past its needed-by date. Kept
  // to one addition (not two) so the pre-existing suspect-year fixture
  // below (736105, first in `atRiskJobs`) stays inside the "At risk / Late"
  // lane's default preview cap of 5 — `at_risk_late` is
  // `[...overdueJobs, ...atRiskJobs]`, so a longer `overdueJobs` pushes it
  // further down.
  job({
    id: "cw-736705",
    job_no: "736705",
    customer_name: "Seabrook Retail",
    job_title: "Autumn window display boards — print & finish",
    rag: "red",
    required_at: d(-15),
    quantity: 20,
    readiness: "in_production",
    top_alert_label: "Past due date",
  }),
].map(withComms);

// ---------------------------------------------------------------------------
// Due today — required_at is today
// ---------------------------------------------------------------------------

export const dueTodayJobs: WorkUnitSummaryItem[] = [
  job({
    id: "cw-730001",
    job_no: "730001",
    customer_name: "Kenton Care",
    customer_id: "c-kenton-care",
    job_title: "Patient information leaflets",
    rag: "amber",
    required_at: d(0),
  }),
  job({
    id: "cw-730002",
    job_no: "730002",
    customer_name: "Pollards Direct",
    customer_id: "c-pollards-direct",
    job_title: "Catalogue inserts — summer edition",
    rag: "green",
    required_at: d(0),
  }),
  job({
    id: "cw-730003",
    job_no: "730003",
    customer_name: "Green Valley Homes",
    customer_id: "c-green-valley",
    job_title: "New development floor plan packs",
    rag: "amber",
    required_at: d(0),
  }),
  job({
    id: "cw-730004",
    job_no: "730004",
    customer_name: "NHS Trust",
    customer_id: "c-nhs-trust",
    job_title: "Staff briefing documents — Q2",
    rag: "green",
    required_at: d(0),
  }),
  job({
    id: "cw-730005",
    job_no: "730005",
    customer_name: "Meridian Group",
    customer_id: "c-meridian",
    job_title: "Annual conference delegate pack",
    rag: "amber",
    required_at: d(0),
  }),
].map(withComms);

// ---------------------------------------------------------------------------
// Due soon — required_at within next 3 working days
// ---------------------------------------------------------------------------

export const dueSoonJobs: WorkUnitSummaryItem[] = [
  job({
    id: "cw-730010",
    job_no: "730010",
    customer_name: "Acme Ltd",
    customer_id: "c-acme",
    job_title: "Product launch flyers — Q3",
    rag: "green",
    required_at: d(1),
  }),
  job({
    id: "cw-730011",
    job_no: "730011",
    customer_name: "Kenton Care",
    customer_id: "c-kenton-care",
    job_title: "KC Summer Newsletter covers",
    rag: "amber",
    required_at: d(1),
  }),
  job({
    id: "cw-730012",
    job_no: "730012",
    customer_name: "Brightside Health",
    customer_id: "c-brightside",
    job_title: "Outpatient registration forms",
    rag: "green",
    required_at: d(2),
  }),
  job({
    id: "cw-730013",
    job_no: "730013",
    customer_name: "Pollards Direct",
    customer_id: "c-pollards-direct",
    job_title: "Loyalty reward mailers",
    rag: "green",
    required_at: d(2),
  }),
  job({
    id: "cw-730014",
    job_no: "730014",
    customer_name: "Green Valley Homes",
    customer_id: "c-green-valley",
    job_title: "New build welcome info packs",
    rag: "amber",
    required_at: d(3),
  }),
  job({
    id: "cw-730015",
    job_no: "730015",
    customer_name: "Meridian Group",
    customer_id: "c-meridian",
    job_title: "Internal comms packs — July",
    rag: "green",
    required_at: d(3),
  }),
  job({
    id: "cw-730016",
    job_no: "730016",
    customer_name: "NHS Trust",
    customer_id: "c-nhs-trust",
    job_title: "Clinical guidelines update — v4",
    rag: "green",
    required_at: d(3),
  }),
  job({
    id: "cw-730017",
    job_no: "730017",
    customer_name: "Clarke & Son",
    customer_id: "c-clarke",
    job_title: "Exhibition pull-up banners",
    rag: "green",
    required_at: d(3),
  }),
  // Real job 732198.01 — 63 days on proof with the customer and no work
  // alert has fired (rag stays green; the deadline badge below is driven by
  // required_at alone, a separate axis). Demonstrates the reason line's
  // no-reason fallback for out_for_proof: "With the customer 63 days".
  job({
    id: "cw-732198-01",
    job_no: "732198.01",
    customer_name: "The Veterans Foundation",
    job_title: "Fundraising appeal pack — proof pending",
    rag: "green",
    required_at: d(3),
    quantity: 2348,
    readiness: "out_for_proof",
    readiness_since: d(-63),
    top_alert_label: null,
  }),
  // Real job 736016.03 — ready to start, but its top alert already names a
  // late stage rather than a bare RAG word.
  job({
    id: "cw-736016-03",
    job_no: "736016.03",
    customer_name: "The Donkey Sanctuary",
    job_title: "Legacy giving brochure — reprint",
    rag: "amber",
    required_at: d(3),
    quantity: 70_000,
    readiness: "production_ready",
    top_alert_label: "Stage running late",
  }),
  // Real job 738889 — ready to start, on track, no open alert.
  job({
    id: "cw-738889",
    job_no: "738889",
    customer_name: "The Guinness Partnership",
    job_title: "Resident newsletter — autumn edition",
    rag: "green",
    required_at: d(2),
    readiness: "production_ready",
    top_alert_label: null,
  }),
  // Real job 737193.02 — already in production, on track.
  job({
    id: "cw-737193-02",
    job_no: "737193.02",
    customer_name: "Rowcroft House Foundation",
    job_title: "Hospice fundraising leaflets",
    rag: "green",
    required_at: d(3),
    quantity: 6_635,
    readiness: "in_production",
    top_alert_label: null,
  }),
].map(withComms);

// ---------------------------------------------------------------------------
// At risk — rag amber, due > 3 working days out
// ---------------------------------------------------------------------------

export const atRiskJobs: WorkUnitSummaryItem[] = [
  // Real job 736105: the customer needs it by 29 Aug 2026, but the Tharstern
  // required-by date was keyed a year early. The backend keeps a suspect-year
  // job OUT of `overdue` and surfaces it here in `at_risk` (a human needs to
  // check it); the board shows a quiet "check in Tharstern" hint, not a huge
  // OVERDUE badge computed off the mis-keyed date.
  job({
    id: "cw-736105",
    job_no: "736105",
    customer_name: "Seasalt Limited",
    customer_id: "c-seasalt-limited",
    job_title: "Monthly Lookbook Returns — August 2026",
    rag: "amber",
    required_at: "2025-08-29T00:00:00.000Z",
    needed_by_year_suspect: true,
  }),
  job({
    id: "cw-730020",
    job_no: "730020",
    customer_name: "Acme Ltd",
    customer_id: "c-acme",
    job_title: "Trade catalogue — autumn edition",
    rag: "amber",
    // Must stay beyond the due-soon cutoff (max 5 calendar days out — see
    // mockMatchesDueWindow's due_soon case) — this lane's own invariant
    // (header comment: "due > 3 working days out") and the real backend's
    // get_urgency_buckets CASE, which only reaches the at_risk branch for a
    // job NOT already due_today/due_soon-bucketed. d(5) intermittently
    // violated this depending on the day of the week the mock runs, which
    // let it leak into the due-soon-filtered jobs list without counting on
    // the due-soon tile once atRiskJobs joined the work-portfolio mock's
    // population (see work-visibility.ts's `_TILE_ANCHOR_ROWS`).
    required_at: d(6),
  }),
  // On the board only because the viewer booked it in — the customer is
  // allocated to the "Group CSR" catch-all, not to them. Before the work-scope
  // widening this job reached nobody's dashboard at all, which is the case Jon
  // raised: "once a job is booked in we tend to see it through".
  job({
    id: "cw-731884",
    job_no: "731884",
    customer_name: "Lymphoma Action",
    customer_id: "c-lymphoma-action",
    job_title: "Donor welcome pack — reprint",
    rag: "amber",
    // See 730020's comment above — must stay beyond the due-soon cutoff.
    required_at: d(6),
    booked_by_you: true,
  }),
  job({
    id: "cw-730021",
    job_no: "730021",
    customer_name: "Kenton Care",
    customer_id: "c-kenton-care",
    job_title: "Patient consent form update",
    rag: "amber",
    // See 730020's comment above — must stay beyond the due-soon cutoff.
    required_at: d(6),
  }),
  job({
    id: "cw-730022",
    job_no: "730022",
    customer_name: "Brightside Health",
    customer_id: "c-brightside",
    job_title: "Ward signage — new wing",
    rag: "amber",
    required_at: d(6),
  }),
  job({
    id: "cw-730023",
    job_no: "730023",
    customer_name: "NHS Trust",
    customer_id: "c-nhs-trust",
    job_title: "Patient pathway booklets",
    rag: "amber",
    required_at: d(7),
  }),
  job({
    id: "cw-730024",
    job_no: "730024",
    customer_name: "Meridian Group",
    customer_id: "c-meridian",
    job_title: "Investor relations pack",
    rag: "amber",
    // See 730020's comment above — must stay beyond the due-soon cutoff.
    required_at: d(6),
  }),
  job({
    id: "cw-730025",
    job_no: "730025",
    customer_name: "Green Valley Homes",
    customer_id: "c-green-valley",
    job_title: "Sales office display boards",
    rag: "amber",
    required_at: d(8),
  }),
  job({
    id: "cw-730026",
    job_no: "730026",
    customer_name: "Clarke & Son",
    customer_id: "c-clarke",
    job_title: "Corporate stationery reprint",
    rag: "amber",
    required_at: d(6),
  }),
  job({
    id: "cw-730027",
    job_no: "730027",
    customer_name: "Pollards Direct",
    customer_id: "c-pollards-direct",
    job_title: "Christmas catalogue inserts",
    rag: "amber",
    required_at: d(9),
  }),
  job({
    id: "cw-730028",
    job_no: "730028",
    customer_name: "Acme Ltd",
    customer_id: "c-acme",
    job_title: "Retail point-of-sale materials",
    rag: "amber",
    required_at: d(7),
  }),
  job({
    id: "cw-730029",
    job_no: "730029",
    customer_name: "NHS Trust",
    customer_id: "c-nhs-trust",
    job_title: "Vaccination campaign leaflets",
    rag: "amber",
    required_at: d(10),
  }),
  job({
    id: "cw-730030",
    job_no: "730030",
    customer_name: "Kenton Care",
    customer_id: "c-kenton-care",
    job_title: "Resident newsletter — August",
    rag: "amber",
    required_at: d(8),
  }),
  job({
    id: "cw-730031",
    job_no: "730031",
    customer_name: "Brightside Health",
    customer_id: "c-brightside",
    job_title: "Discharge information packs",
    rag: "amber",
    required_at: d(9),
  }),
  // Real job 736242 — out for proof for over a month, and this time the
  // top alert does name a reason (unlike 732198.01's no-reason fallback
  // above), so the reason line reads "Due date approaching · 38 days".
  job({
    id: "cw-736242",
    job_no: "736242",
    customer_name: "Ramsay Health Care UK",
    job_title: "Patient discharge leaflets — reprint",
    rag: "amber",
    // See 730020's comment above — must stay beyond the due-soon cutoff.
    required_at: d(6),
    readiness: "out_for_proof",
    readiness_since: d(-38),
    top_alert_label: "Due date approaching",
  }),
].map(withComms);

// ---------------------------------------------------------------------------
// WIP — active, green RAG, due > 3 working days
// ---------------------------------------------------------------------------

export const wipJobs: WorkUnitSummaryItem[] = [
  job({
    id: "cw-730040",
    job_no: "730040",
    customer_name: "Acme Ltd",
    customer_id: "c-acme",
    job_title: "Autumn product catalogue",
    rag: "green",
    required_at: d(8),
  }),
  job({
    id: "cw-730041",
    job_no: "730041",
    customer_name: "Kenton Care",
    customer_id: "c-kenton-care",
    job_title: "Resident information packs — September",
    rag: "green",
    required_at: d(10),
    // Visibility-provenance example — a House-Account/catch-all customer,
    // visible to every CSR rather than scoped to one portfolio.
    visibility_reason: "catch_all",
  }),
  job({
    id: "cw-730042",
    job_no: "730042",
    customer_name: "Brightside Health",
    customer_id: "c-brightside",
    job_title: "Referral pathway booklets v2",
    rag: "green",
    required_at: d(7),
  }),
  job({
    id: "cw-730043",
    job_no: "730043",
    customer_name: "NHS Trust",
    customer_id: "c-nhs-trust",
    job_title: "Estates signage — phase 2",
    rag: "green",
    required_at: d(12),
  }),
  job({
    id: "cw-730044",
    job_no: "730044",
    customer_name: "Meridian Group",
    customer_id: "c-meridian",
    job_title: "Q3 shareholder newsletter",
    rag: "green",
    required_at: d(9),
  }),
  job({
    id: "cw-730045",
    job_no: "730045",
    customer_name: "Pollards Direct",
    customer_id: "c-pollards-direct",
    job_title: "Back-to-school campaign mailers",
    rag: "green",
    required_at: d(6),
  }),
  job({
    id: "cw-730046",
    job_no: "730046",
    customer_name: "Green Valley Homes",
    customer_id: "c-green-valley",
    job_title: "Plot completion welcome packs",
    rag: "green",
    required_at: d(14),
  }),
  job({
    id: "cw-730047",
    job_no: "730047",
    customer_name: "Clarke & Son",
    customer_id: "c-clarke",
    job_title: "Summer sale point-of-sale",
    rag: "green",
    // Must stay beyond the due-soon cutoff (max 5 calendar days out — see
    // mockMatchesDueWindow's due_soon case): the real backend's
    // get_wip_jobs requires `due_at > due_soon_cutoff OR NULL` by
    // construction (WIP is healthy, non-imminent work), so a WIP fixture
    // due within the window violates the lane's own invariant. d(5)
    // intermittently violated this depending on the day of the week the
    // mock runs, which let it leak into the due-soon-filtered jobs list
    // without counting on the due-soon tile once wipJobs joined the
    // work-portfolio mock's population (see work-visibility.ts's
    // `_TILE_ANCHOR_ROWS`).
    required_at: d(6),
  }),
  job({
    id: "cw-730048",
    job_no: "730048",
    customer_name: "Acme Ltd",
    customer_id: "c-acme",
    job_title: "Trade show pull-up banners",
    rag: "green",
    required_at: null,
  }),
  // Real job 738031 — non-press work (factored goods), never destined for
  // the readiness ladder. The card shows its work_kind ("Factored")
  // instead of a readiness badge that would never apply.
  job({
    id: "cw-738031",
    job_no: "738031",
    customer_name: "Holcim UK Limited",
    job_title: "Factored merchandise — site hi-vis kits",
    rag: "green",
    required_at: d(6),
    readiness: "non_press",
    work_kind: "Factored",
    top_alert_label: null,
  }),
  // WIP is green-RAG by definition (no open alert), but readiness is a
  // separate axis — a healthy job can still be stuck waiting to start.
  // top_alert_label stays null (the real backend's invariant: null iff
  // rag === green); boardReasonLine falls back to the readiness-worded
  // line instead, so this card reads "Booked 95 days ago · nothing
  // started" even though its deadline badge stays quiet. Modelled on the
  // real not-production-ready-but-not-yet-urgent jobs that make up most of
  // a live not_production_ready backlog.
  job({
    id: "cw-736710",
    job_no: "736710",
    customer_name: "Fenwick Transport",
    job_title: "Compliance signage — depot rollout",
    rag: "green",
    required_at: d(12),
    quantity: 40,
    readiness: "not_production_ready",
    readiness_since: d(-95),
    top_alert_label: null,
  }),
  // Same idea, out-for-proof flavour — sitting with the customer for proof
  // sign-off, due date still comfortably out.
  job({
    id: "cw-736712",
    job_no: "736712",
    customer_name: "Bellcourt Retail",
    job_title: "Loyalty card reprint — awaiting proof sign-off",
    rag: "green",
    required_at: d(9),
    quantity: 2000,
    readiness: "out_for_proof",
    readiness_since: d(-21),
    top_alert_label: null,
  }),
]
  .map(withComms)
  // Worst-first by due date, matching the real backend's WIP ordering
  // (`due_at ASC NULLS LAST`) — the literals above aren't authored in that
  // order, so re-sort rather than require every future addition to be
  // inserted at the exact right position.
  .sort((a, b) => {
    const at = a.required_at ? new Date(a.required_at).getTime() : Infinity;
    const bt = b.required_at ? new Date(b.required_at).getTime() : Infinity;
    return at - bt;
  });

// ---------------------------------------------------------------------------
// Recent completions — ready to despatch / out for delivery / delivered / invoiced
// ---------------------------------------------------------------------------

export const recentCompletions: WorkUnitSummaryItem[] = [
  job({
    id: "cw-729852",
    job_no: "729852",
    customer_name: "Acme Ltd",
    customer_id: "c-acme",
    job_title: "Trade brochures — Q3 launch",
    rag: "green",
    required_at: d(-1),
    completion_state: "Ready to despatch",
  }),
  job({
    id: "cw-729870",
    job_no: "729870",
    customer_name: "Kenton Care",
    customer_id: "c-kenton-care",
    job_title: "Staff handbook — 2024 edition",
    // A finished job can still carry a surviving open alert — demonstrates
    // the alt card's alert line on a "Recently completed" row, and that
    // `attention` only goes amber from the alert here, never from the (now
    // moot) due date — see `_compute_attention`'s `is_finished` guard.
    rag: "amber",
    required_at: d(-1),
    completion_state: "Out for delivery",
    top_alert_label: "Query raised post-delivery",
  }),
  job({
    id: "cw-729100",
    job_no: "729100",
    customer_name: "Clarke & Son",
    customer_id: "c-clarke",
    job_title: "Exhibition flyers — summer show",
    rag: "green",
    required_at: d(-2),
    completion_state: "Delivered",
  }),
  job({
    id: "cw-729810",
    job_no: "729810",
    customer_name: "Green Valley Homes",
    customer_id: "c-green-valley",
    job_title: "Site hoarding graphics",
    rag: "green",
    required_at: d(-2),
    completion_state: "Out for delivery",
  }),
  job({
    id: "cw-728900",
    job_no: "728900",
    customer_name: "NHS Trust",
    customer_id: "c-nhs-trust",
    job_title: "Training materials pack — cohort 4",
    rag: "green",
    required_at: d(-4),
    completion_state: "Invoiced",
  }),
  job({
    id: "cw-729720",
    job_no: "729720",
    customer_name: "Brightside Health",
    customer_id: "c-brightside",
    job_title: "Appointment reminder cards",
    rag: "green",
    required_at: d(-4),
    completion_state: "Delivered",
  }),
  job({
    id: "cw-728750",
    job_no: "728750",
    customer_name: "Meridian Group",
    customer_id: "c-meridian",
    job_title: "Conference delegate packs",
    // Red from a surviving open alert, not from the due date (which is
    // moot — the job finished days ago) — the finished-job red-only-from-
    // alert branch of `_compute_attention`.
    rag: "red",
    required_at: d(-5),
    completion_state: "Delivered",
    top_alert_label: "Invoice query unresolved",
  }),
  job({
    id: "cw-729600",
    job_no: "729600",
    customer_name: "Pollards Direct",
    customer_id: "c-pollards-direct",
    job_title: "Spring catalogue — main run",
    rag: "green",
    required_at: d(-6),
    completion_state: "Invoiced",
  }),
  job({
    id: "cw-729480",
    job_no: "729480",
    customer_name: "Acme Ltd",
    customer_id: "c-acme",
    job_title: "Product spec sheets — refreshed range",
    rag: "green",
    required_at: d(-7),
    completion_state: "Invoiced",
  }),
].map(withComms);

// ---------------------------------------------------------------------------
// Finished — awaiting close. Jobs that have finished production or reached
// the customer but aren't closed off yet. The backend drops these from the
// risk buckets so they no longer read as "overdue" past their needed-by date
// (2026-09-10 widening — a produced-but-not-yet-delivered job used to keep
// aging into OVERDUE forever); each carries a `close_state` explaining why it
// is still open (awaiting despatch / awaiting invoice / invoiced and closing /
// booked into stock). Rendered NEUTRAL — never red.
// ---------------------------------------------------------------------------

export const finishedAwaitingClose: WorkUnitSummaryItem[] = [
  job({
    id: "cw-728395",
    job_no: "728395",
    customer_name: "Brightside Retail",
    customer_id: "c-brightside",
    job_title: "Point-of-sale posters — reprint",
    rag: "green",
    required_at: d(-4),
    close_state: "awaiting_despatch",
  }),
  job({
    id: "cw-728397",
    job_no: "728397",
    customer_name: "Kenton Care",
    customer_id: "c-kenton-care",
    job_title: "Care plan folders — Q3 top-up",
    rag: "green",
    required_at: d(-9),
    close_state: "awaiting_despatch",
  }),
  job({
    id: "cw-728400",
    job_no: "728400",
    customer_name: "Acme Ltd",
    customer_id: "c-acme",
    job_title: "Spring mailer — main run",
    rag: "green",
    required_at: d(-3),
    close_state: "awaiting_invoice",
  }),
  job({
    id: "cw-728410",
    job_no: "728410",
    customer_name: "Kenton Care",
    customer_id: "c-kenton-care",
    job_title: "Care plan folders — Q2",
    rag: "green",
    required_at: d(-6),
    close_state: "awaiting_invoice",
  }),
  job({
    id: "cw-728420",
    job_no: "728420",
    customer_name: "NHS Trust",
    customer_id: "c-nhs-trust",
    job_title: "Ward wayfinding signage",
    rag: "green",
    required_at: d(-8),
    close_state: "invoiced",
  }),
  job({
    id: "cw-728430",
    job_no: "728430",
    customer_name: "Meridian Group",
    customer_id: "c-meridian",
    job_title: "AGM delegate packs",
    rag: "green",
    required_at: d(-11),
    close_state: "invoiced",
  }),
  job({
    id: "cw-728440",
    job_no: "728440",
    customer_name: "Green Valley Homes",
    customer_id: "c-green-valley",
    job_title: "Show-home brochure stock reprint",
    rag: "green",
    required_at: d(-5),
    close_state: "into_stock",
  }),
  job({
    id: "cw-728450",
    job_no: "728450",
    customer_name: "Clarke & Son",
    customer_id: "c-clarke",
    job_title: "Branded folders — held stock",
    rag: "green",
    required_at: d(-9),
    close_state: "into_stock",
  }),
].map(withComms);

// ---------------------------------------------------------------------------
// Not production ready — booked with no production milestone and no
// production-ready tick yet. Previously invisible to the board entirely
// (see docs/features/csr-command-centre/): a job with no milestone fell out
// of every deadline bucket above, which is exactly what left 211 real
// in-flight jobs unrendered. `not_production_ready` on the dashboard is a
// capped, worst-first-by-due-date preview of this array — the true count
// lives on `summary_counts.not_production_ready_count`, not this array's
// length. `readiness_since` mirrors `ordered_at` for this readiness value.
// ---------------------------------------------------------------------------

export const notProductionReadyJobs: WorkUnitSummaryItem[] = [
  // Real job 730051 — 107 days overdue, booked 113 days ago, nothing
  // scheduled since.
  job({
    id: "cw-730051",
    job_no: "730051",
    customer_name: "British Kidney Patient Association",
    job_title: "Patient support information pack",
    rag: "red",
    required_at: d(-107),
    readiness: "not_production_ready",
    readiness_since: d(-113),
    top_alert_label: "Past due date",
  }),
  // Real job 723416 — 11 days overdue, booked 191 days ago.
  job({
    id: "cw-723416",
    job_no: "723416",
    customer_name: "Home Group Limited",
    job_title: "Tenant handbook — 2026 update",
    rag: "red",
    required_at: d(-11),
    readiness: "not_production_ready",
    readiness_since: d(-191),
    top_alert_label: "Past due date",
  }),
  // Real job 732128 — due today, booked 87 days ago.
  job({
    id: "cw-732128",
    job_no: "732128",
    customer_name: "Ramsay Health Care UK",
    job_title: "Ward welcome packs",
    rag: "red",
    required_at: d(0),
    quantity: 1000,
    readiness: "not_production_ready",
    readiness_since: d(-87),
    top_alert_label: "Due date approaching",
  }),
  // Real job 738345 — due in 2 days, Stock Approval is the named blocker.
  job({
    id: "cw-738345",
    job_no: "738345",
    customer_name: "Holcim UK Limited",
    job_title: "Site safety signage — annual refresh",
    rag: "red",
    required_at: d(2),
    readiness: "not_production_ready",
    readiness_since: d(-14),
    waiting_reason: "Waiting for Stock Approval",
    top_alert_label: "Due date approaching",
  }),
  // Real job 727501.07 — due in 3 days, booked 148 days ago, no alert has
  // fired and nothing is named as blocking it. Demonstrates the reason
  // line's no-reason fallback for not_production_ready: "Booked 148 days
  // ago · nothing started".
  job({
    id: "cw-727501-07",
    job_no: "727501.07",
    customer_name: "Stratos Creative Limited",
    job_title: "Retail display units — panel set",
    rag: "green",
    required_at: d(3),
    readiness: "not_production_ready",
    readiness_since: d(-148),
    top_alert_label: null,
  }),
  // Real job 738420 — due in 4 days, booked only a week ago, waiting on
  // artwork files from the customer.
  job({
    id: "cw-738420",
    job_no: "738420",
    customer_name: "British Gas",
    job_title: "Customer billing insert — September run",
    rag: "amber",
    required_at: d(4),
    quantity: 10_000,
    readiness: "not_production_ready",
    readiness_since: d(-7),
    waiting_reason: "Waiting for files",
    top_alert_label: "Due date approaching",
  }),
  // Real job 734538.01 — due in 5 days, booked 59 days ago, a very large run.
  job({
    id: "cw-734538-01",
    job_no: "734538.01",
    customer_name: "Seasalt Limited",
    job_title: "Autumn catalogue mailer — main run",
    rag: "amber",
    required_at: d(5),
    quantity: 215_500,
    readiness: "not_production_ready",
    readiness_since: d(-59),
    top_alert_label: "Due date approaching",
  }),
].map(withComms);

// ---------------------------------------------------------------------------
// Work alerts — system-detected job issues
// ---------------------------------------------------------------------------

export const workAlerts: WorkAlertItem[] = [
  {
    id: "wa-001",
    job_no: "729501",
    customer_name: "Kenton Care",
    customer_id: "c-kenton-care",
    customer_url: "/customers/c-kenton-care",
    job_title: "Q3 quarterly newsletter",
    message: "Stuck in Pre-press for 9 days — no state change since 8 June",
    alert_type: "stuck_stage",
    severity: "red",
    self_url: "/jobs/729501",
    detected_at: d(-9),
    required_at: d(-2),
    rag: "red",
  },
  {
    id: "wa-002",
    job_no: "729988",
    customer_name: "Acme Ltd",
    customer_id: "c-acme",
    customer_url: "/customers/c-acme",
    job_title: "Retail shelf-edge labels",
    message: "Automation switch failed — manual booking required",
    alert_type: "switch_failed",
    severity: "red",
    self_url: "/jobs/729988",
    detected_at: hoursAgo(5),
    required_at: d(1),
    rag: "amber",
  },
  {
    id: "wa-003",
    job_no: "730033",
    customer_name: "Brightside Health",
    customer_id: "c-brightside",
    customer_url: "/customers/c-brightside",
    job_title: "Ward directory boards",
    message: "4 unresolved exceptions — oldest raised 5 days ago",
    alert_type: "exception",
    severity: "amber",
    self_url: "/jobs/730033",
    detected_at: d(-5),
    required_at: d(4),
    rag: "amber",
  },
  {
    id: "wa-004",
    job_no: "730041",
    customer_name: "NHS Trust",
    customer_id: "c-nhs-trust",
    customer_url: "/customers/c-nhs-trust",
    job_title: "Visitor guidance leaflets",
    message: "Stuck in Materials for 6 days — supplier delivery overdue",
    alert_type: "stuck_stage",
    severity: "amber",
    self_url: "/jobs/730041",
    detected_at: d(-6),
    required_at: null,
    rag: "amber",
  },
];

// ---------------------------------------------------------------------------
// Active tickets assigned to the caller (any non-closed/resolved status —
// not just status="open"), projected from the single canonical ticket store
// (tickets.ts's mockTickets) rather than a second hand-authored set.
//
// This used to be its own hand-authored TicketItem[] with rail-local ids
// ("ticket-001", hyphen) that never matched mockTickets' ids ("ticket_001",
// underscore) — every rail link 404'd on the ticket detail page, which reads
// through mockTickets via getTicketView. Deriving from mockTickets makes
// that drift impossible by construction: PORTFOLIO_TICKET_IDS below throws
// at import time if any id it names ever falls out of mockTickets, and
// mock-adapter.tickets-rail.test.ts asserts every projected self_url
// resolves in the ticket store.
// ---------------------------------------------------------------------------

const customerNameById = new Map(mockCustomers.map((c) => [c.id, c.name]));
const bookedOrderById = new Map(mockBookedOrders.map((o) => [o.id, o]));

function ticketItemFromMockTicket(ticket: Ticket): TicketItem {
  // Mirrors the backend's own rule for job_no/job_due_at/job_url (see
  // views_module/api/transformers/csr_home.py's _ticket_from_dict): they are
  // populated only when the ticket's subject really is a job AND that job
  // still resolves — all three null together otherwise. due_date is the
  // ticket's own (frequently null) deadline, a distinct thing from the
  // linked job's own due date.
  const linkedOrder =
    ticket.subject_type === "job" && ticket.subject_id
      ? bookedOrderById.get(ticket.subject_id)
      : undefined;
  const jobNo = linkedOrder?.source_system_tharstern_job_number || null;
  const hasLinkedJob = jobNo != null;

  return {
    id: ticket.id,
    title: ticket.title,
    customer_id: ticket.customer_id ?? null,
    customer_name: ticket.customer_id
      ? (customerNameById.get(ticket.customer_id) ?? null)
      : null,
    due_date: ticket.due_date ?? null,
    created_at: ticket.created_at,
    created_by_user_id: ticket.created_by_user_id,
    self_url: `/tickets/${ticket.id}`,
    job_no: hasLinkedJob ? jobNo : null,
    job_due_at: hasLinkedJob ? (linkedOrder?.due_date ?? null) : null,
    job_url: hasLinkedJob ? `/jobs/${jobNo}` : null,
  };
}

// Curated subset for the rail — the states its design needs to exercise
// (due today, no due date of its own, urgent/alert-raised, overdue, and one
// with a linked job), not every active ticket assigned to the caller in
// tickets.ts. `.find()` below throws at import time rather than silently
// dropping a row if one of these ids ever moves or is removed.
const PORTFOLIO_TICKET_IDS = [
  "ticket_002", // due today, high priority
  "ticket_021", // linked job (48291); no due date of its own
  "ticket_020", // overdue
  "ticket_007", // due today, urgent, alert-raised
  "ticket_004", // waiting on the customer, no due date
  "ticket_008", // low priority, no due date
  "ticket_015", // campaign-engaged, due today
];

export const portfolioTickets: TicketItem[] = PORTFOLIO_TICKET_IDS.map((id) => {
  const ticket = mockTickets.find((t) => t.id === id);
  if (!ticket) {
    throw new Error(
      `csr-home mock: portfolioTickets references missing ticket id "${id}" — check tickets.ts`,
    );
  }
  return ticketItemFromMockTicket(ticket);
});

// ---------------------------------------------------------------------------
// Upcoming-meetings rail — the CSR's next meetings (not just today's),
// correlated to dashboard jobs by customer_id (the mock stand-in for the
// real composed view's join). Spans several days so the rail visibly reads
// as "upcoming", not "today".
// ---------------------------------------------------------------------------

const CUSTOMER_TO_UPCOMING_JOB_NO: Record<string, string> = {
  "c-meridian": "730005",
  "c-kenton-care": "730001",
};

export const upcomingMeetings: UpcomingMeetingItem[] =
  csrCommandCentreCalendarEvents.map((event) => ({
    id: event.id,
    title: event.title,
    start_at: event.start_at,
    end_at: event.end_at,
    location: event.location,
    attendee_count: event.attendees.length,
    web_link: `https://outlook.office.com/calendar/item/${event.id}`,
    // Only an event with an online component carries a join url — the rail's
    // Join action is absent for the phone/in-person ones.
    online_meeting_join_url:
      event.location === "Microsoft Teams"
        ? `https://teams.microsoft.com/l/meetup-join/${event.id}`
        : null,
    job_no: CUSTOMER_TO_UPCOMING_JOB_NO[event.customer_id] ?? null,
  }));

// ---------------------------------------------------------------------------
// Tile counts — overdue/due-today/due-soon.
//
// Not simply `overdueJobs.length` etc: a job can legitimately be BOTH
// `not_production_ready` (or `at_risk`/WIP) AND overdue/due-today/due-soon
// (readiness and urgency are separate, orthogonal axes — see
// job_readiness.py's own docstrings), and several of `notProductionReadyJobs`'
// real-pattern entries genuinely are. The real backend's `get_urgency_counts`
// is an independent, uncapped query over the WHOLE active population, so its
// count can legitimately exceed the matching capped-preview lane's own
// array length (exactly like `not_production_ready_count` already does
// against `notProductionReadyJobs.length`) — this mirrors that by counting
// over EVERY lane/WIP array (overdue/dueToday/dueSoon/atRisk/wip/
// notProductionReady — the same population work-visibility.ts's
// `mockPortfolioRows` serves for its own `due_window` filter, via
// `_TILE_ANCHOR_ROWS`), not just the lane that owns the deadline axis. Doing
// this is also what keeps these counts agreeing with the work-portfolio
// mock's `due_window` filter, so a CsrHomeAlt stat tile always matches the
// jobs-page list it links to.
//
// Including atRiskJobs here means 736105 (the needed-by-year-suspect job,
// required_at a real past date) is now counted as "overdue" by this tile,
// same as `mockMatchesDueWindow`'s generic "overdue" case counts it in the
// work-portfolio list — deliberately NOT excluded the way readiness_pipeline's
// own overdue_count below excludes it. That's a different, narrower axis
// (see that field's comment); this tile's job is to agree with the
// due_window-filtered list byte-for-byte, and the list has no year-suspect
// exception of its own.
//
// Every OTHER atRisk/wip job must keep its required_at beyond the due-soon
// cutoff (max 5 calendar days out) — that's each lane's own invariant (see
// 730020/730047's own comments) and the real backend's bucket CASE, which
// only reaches at_risk/WIP for a job not already due-bucketed — so none of
// them double up into these counts.
//
// Reimplements (rather than imports) work-visibility.ts's
// `mockMatchesDueWindow` due-soon cutoff to avoid a circular import
// (work-visibility.ts imports these lane arrays from this file) — keep the
// two in sync if the due-soon window definition ever changes.
// ---------------------------------------------------------------------------

const _TILE_CANDIDATE_JOBS: WorkUnitSummaryItem[] = (() => {
  const seen = new Set<string>();
  return [
    ...overdueJobs,
    ...dueTodayJobs,
    ...dueSoonJobs,
    ...atRiskJobs,
    ...wipJobs,
    ...notProductionReadyJobs,
  ].filter((job) => {
    if (seen.has(job.job_no)) return false;
    seen.add(job.job_no);
    return true;
  });
})();

function _startOfToday(): number {
  return new Date().setHours(0, 0, 0, 0);
}

function _isOverdue(job: WorkUnitSummaryItem): boolean {
  return (
    Boolean(job.required_at) &&
    new Date(job.required_at!).getTime() < _startOfToday()
  );
}

function _isDueToday(job: WorkUnitSummaryItem): boolean {
  if (!job.required_at) return false;
  const t = new Date(job.required_at).getTime();
  return t >= _startOfToday() && t <= new Date().setHours(23, 59, 59, 999);
}

function _isDueSoon(job: WorkUnitSummaryItem): boolean {
  if (!job.required_at) return false;
  const t = new Date(job.required_at).getTime();
  const endOfToday = new Date().setHours(23, 59, 59, 999);
  const dueSoonCutoff = addWorkingDays(new Date(_startOfToday()), 3).setHours(
    23,
    59,
    59,
    999,
  );
  return t > endOfToday && t <= dueSoonCutoff;
}

const _overdueCount = _TILE_CANDIDATE_JOBS.filter(_isOverdue).length;
const _dueTodayCount = _TILE_CANDIDATE_JOBS.filter(_isDueToday).length;
const _dueSoonCount = _TILE_CANDIDATE_JOBS.filter(_isDueSoon).length;

// ---------------------------------------------------------------------------
// In-flight board — the alt CSR-home page's single combined worklist
// (csr-dashboard-alt-screen): every non-finished in-flight job as one
// red/amber/green worst-first list, replacing the alt page's old WIP-only
// main content (which, being WIP by definition, could only ever render
// green cards). Mirrors the real backend's get_in_flight_board population
// (views_module.data_adapters.postgres_ro.csr_home_query): the
// de-duplicated union of every lane/WIP array above EXCEPT
// recentCompletions/finishedAwaitingClose (finished work stays exclusively
// in those two lanes, never duplicated here) — not the RAW_PORTFOLIO_ROWS
// anchor fixtures work-visibility.ts adds on top for the jobs-page mock,
// which have no real backend equivalent on this dashboard endpoint. No two
// of the six arrays share a job_no today, but the dedup guards the same way
// the real SQL's single population does.
//
// Sorted worst-first: attention red before amber before green (the same
// `attention` field every card already carries, computed per-job by
// `defaultAttention` above — not re-derived here), then required_at
// ascending (nulls last), then job_no as a stable tiebreak — mirroring
// `views_module.api.transformers.csr_home._in_flight_board_sort_key`.
// ---------------------------------------------------------------------------

const _ATTENTION_ORDER: Record<WorkUnitSummaryItem["attention"], number> = {
  red: 0,
  amber: 1,
  green: 2,
};

function _inFlightBoardSortKey(
  job: WorkUnitSummaryItem,
): [number, number, string] {
  const dueTime = job.required_at
    ? new Date(job.required_at).getTime()
    : Infinity;
  return [_ATTENTION_ORDER[job.attention], dueTime, job.job_no];
}

function _compareSortKeys(
  a: [number, number, string],
  b: [number, number, string],
): number {
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return a[i] < b[i] ? -1 : 1;
  }
  return 0;
}

export const inFlightBoard: WorkUnitSummaryItem[] = (() => {
  const seen = new Set<string>();
  const combined = [
    ...overdueJobs,
    ...dueTodayJobs,
    ...dueSoonJobs,
    ...atRiskJobs,
    ...wipJobs,
    ...notProductionReadyJobs,
  ].filter((job) => {
    if (seen.has(job.job_no)) return false;
    seen.add(job.job_no);
    return true;
  });
  return combined.sort((a, b) =>
    _compareSortKeys(_inFlightBoardSortKey(a), _inFlightBoardSortKey(b)),
  );
})();

// ---------------------------------------------------------------------------
// Sales orders needing attention — Confirmed in Tharstern but not (fully)
// allocated (see docs/features/sales-orders-needing-attention/). CSR-home
// only — never shown on the customer portal, see that doc's "Scope"
// section. Oldest order first, matching the real card's ordering.
// ---------------------------------------------------------------------------

export const salesOrdersNeedingAttention: SalesOrderNeedingAttentionItem[] = [
  {
    order_no: "838306",
    account_code: "BRAM",
    customer_name: "Bramley & Co",
    customer_ref: "BRAM-217322-BH360",
    order_date: d(-11),
    allocation_status_label: "Unallocated",
    // Decimal-serialised shape straight from Postgres Numeric(18,4) — not
    // "412.50" — so a story using this mock would have caught a raw
    // `£${net_amount}` render showing "£412.5000".
    net_amount: "412.5000",
    line_count: 1,
    lines_without_job_count: 1,
    job_nos: [],
    self_url: "/customers/BRAM",
  },
  {
    order_no: "838612",
    account_code: "TECH",
    customer_name: "TechStart Ltd",
    customer_ref: "TECH-9931",
    order_date: d(-7),
    allocation_status_label: "Part Allocated",
    net_amount: "1240.0000",
    line_count: 3,
    lines_without_job_count: 1,
    job_nos: ["739812", "739813"],
    self_url: "/customers/TECH",
  },
  {
    order_no: "838701",
    account_code: "HEND",
    customer_name: "Henderson Group",
    customer_ref: null,
    order_date: d(-2),
    allocation_status_label: "Unallocated",
    net_amount: "89.0000",
    line_count: 1,
    lines_without_job_count: 0,
    job_nos: ["739940"],
    self_url: "/customers/HEND",
  },
];

// ---------------------------------------------------------------------------
// Full dashboard response
// ---------------------------------------------------------------------------

export const mockCsrHome: CsrHomeDashboard = {
  // A normal, healthy portfolio — not null (unscoped) and not 0 (the
  // no-customers-assigned incident state; see CsrHome.stories.tsx's
  // NoAssignedCustomers story for that state).
  assigned_customer_count: 47,
  summary_counts: {
    overdue_count: _overdueCount,
    due_today_count: _dueTodayCount,
    due_soon_count: _dueSoonCount,
    at_risk_count: atRiskJobs.length,
    finished_awaiting_close_count: finishedAwaitingClose.length,
    uncleared_alert_count: workAlerts.length,
    active_ticket_count: portfolioTickets.length,
    // Total in-flight jobs — the sum of every `readiness_pipeline` entry's
    // `job_count` below (see that field's comment for how each is derived).
    // Not independent of the rollup: a reader following the readiness strip
    // from a stat tile to a segment and summing should get this number back.
    active_job_count: 72,
    // The true total behind the not_production_ready lane's capped preview
    // (see docs/features/csr-command-centre/). In the real backend this is
    // computed independently by `get_readiness_pipeline` and pinned to agree
    // with this count by a contract test — the mock mirrors that: it equals
    // the number of `readiness: "not_production_ready"` rows the work-portfolio
    // mock (`work-visibility.ts`'s `mockPortfolioRows`) actually returns for
    // `readiness=not_production_ready`, so the CSR-home tile and the jobs-page
    // filtered list it links to always show the same number. Not
    // `notProductionReadyJobs.length` (7) — that array is this lane's own
    // preview, a strict subset of the true population (the other
    // contributors: `wipJobs`' 736710, plus three `RAW_PORTFOLIO_ROWS` anchor
    // fixtures in work-visibility.ts — see `readiness_pipeline` below for the
    // full per-readiness breakdown this agrees with).
    not_production_ready_count: 11,
    sales_orders_needing_attention_count: salesOrdersNeedingAttention.length,
  },
  upcoming_meetings: upcomingMeetings,
  // Readiness rollup for the strip above the board. Replaces stage_pipeline,
  // which silently excluded jobs with no last_milestone — precisely the
  // not-production-ready jobs this axis exists to surface. Ordered by
  // job_count DESC, matching how the real backend rolls this up — the
  // frontend is responsible for re-sorting into ladder order for display.
  //
  // Every entry's job_count/overdue_count/longest_days is pinned to equal
  // the REAL per-readiness population the work-portfolio mock
  // (work-visibility.ts's `mockPortfolioRows`) returns for
  // `?readiness=<value>` — not independently computed here — so that
  // clicking a strip segment always lands on a jobs-page list of exactly
  // that length (same invariant as `not_production_ready_count` above).
  // That population is the de-duplicated union of this file's lane/WIP
  // arrays (overdueJobs, dueTodayJobs, dueSoonJobs, atRiskJobs, wipJobs,
  // notProductionReadyJobs — 55 jobs) PLUS work-visibility.ts's own
  // `RAW_PORTFOLIO_ROWS` anchor fixtures (20 jobs, 3 of which share a
  // job_no with a lane array above and win over it — see that file's
  // `_EXISTING_PORTFOLIO_JOB_NOS` dedup), for 72 unique jobs total.
  // Can't compute this in-file: work-visibility.ts imports the lane arrays
  // from here, so importing its RAW rows back would be a circular import
  // (see the due-soon cutoff's own "reimplements rather than imports"
  // comment above for the same constraint). overdue_count uses the
  // real-backend definition (required_at before today, excluding the one
  // needed-by-year-suspect job); longest_days is the max days since
  // readiness_since across the bucket's jobs (null when none carry one).
  readiness_pipeline: [
    {
      readiness: "in_production",
      job_count: 49,
      overdue_count: 4,
      longest_days: null,
    },
    {
      // Pinned to summary_counts.not_production_ready_count above — see
      // that field's comment.
      readiness: "not_production_ready",
      job_count: 11,
      overdue_count: 3,
      longest_days: 191,
    },
    {
      readiness: "out_for_proof",
      job_count: 5,
      overdue_count: 1,
      longest_days: 63,
    },
    {
      readiness: "production_ready",
      job_count: 4,
      overdue_count: 0,
      longest_days: null,
    },
    {
      readiness: "non_press",
      job_count: 3,
      overdue_count: 0,
      longest_days: null,
    },
  ],
  alerts: workAlerts,
  overdue: overdueJobs,
  due_today: dueTodayJobs,
  due_soon: dueSoonJobs,
  at_risk: atRiskJobs,
  wip: wipJobs,
  recent_completions: recentCompletions,
  finished_awaiting_close: finishedAwaitingClose,
  not_production_ready: notProductionReadyJobs,
  // See inFlightBoard's own comment above for composition/ordering.
  in_flight_board: inFlightBoard,
  in_flight_board_total: inFlightBoard.length,
  tickets: portfolioTickets,
  sales_orders_needing_attention: salesOrdersNeedingAttention,
};

// ---------------------------------------------------------------------------
// Alt CSR-home WIP search/sort (csr-dashboard-alt-screen-0fef93) — mock
// implementation of GET /views/csr-home/in-flight-board. Filters/sorts
// `inFlightBoard` in memory (the mock adapter's job — the real backend does
// this server-side in SQL, see csr_home_query.get_in_flight_board).
// ---------------------------------------------------------------------------

const _READINESS_RANK: Record<string, number> = Object.fromEntries(
  READINESS_LADDER.map((r, i) => [r, i]),
);

// Approximates the backend's real production-milestone ordinal
// (`views_job_snapshot.highest_milestone_type`), which is never sent to the
// frontend — WorkUnitSummaryItem only carries the display label
// (`stage_label`). `_IN_PRODUCTION_STAGE_LABELS` above is already in real
// milestone order, so its index is a faithful-enough proxy for mock/story
// purposes. Every readiness other than `in_production` carries a null
// `stage_label` in this fixture (see `defaultStageLabel`), so they all rank
// equally "not yet reached a stage" — same as the real NULLS FIRST choice.
function _stageRank(job: WorkUnitSummaryItem): number {
  if (job.stage_label === null) return -1;
  const idx = _IN_PRODUCTION_STAGE_LABELS.indexOf(job.stage_label);
  return idx === -1 ? -1 : idx;
}

function _matchesQuery(job: WorkUnitSummaryItem, needle: string): boolean {
  return (
    job.job_no.toLowerCase().includes(needle) ||
    job.customer_name.toLowerCase().includes(needle) ||
    job.job_title.toLowerCase().includes(needle)
  );
}

export function mockInFlightBoardSearch(
  options: InFlightBoardSearchOptions = {},
): InFlightBoardSearchResponse {
  let items = inFlightBoard;
  if (options.q) {
    const needle = options.q.toLowerCase();
    items = items.filter((job) => _matchesQuery(job, needle));
  }

  const sort = options.sort ?? "worst_first";
  const sorted = [...items].sort((a, b) => {
    if (sort === "status") {
      const diff = _READINESS_RANK[a.readiness] - _READINESS_RANK[b.readiness];
      if (diff !== 0) return diff;
    } else if (sort === "stage") {
      const diff = _stageRank(a) - _stageRank(b);
      if (diff !== 0) return diff;
    }
    return _compareSortKeys(_inFlightBoardSortKey(a), _inFlightBoardSortKey(b));
  });

  return { items: sorted, total: sorted.length };
}
